import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsPlus, BsTrash, BsPencil, BsCheck2, BsXLg } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';

import { useDeleteTag, useRenameTag, useCreateTag } from '../../../models/tags/mutations';
import { useTags } from '../../../models/tags/queries';

const TagSettingsRow = ({
	tag,
	isEditing,
	editName,
	onEditNameChange,
	onUpdate,
	onCancelEdit,
	onStartEdit,
	onDelete,
}) => {
	if (isEditing) {
		return (
			<>
				<input
					type="text"
					value={editName}
					onChange={onEditNameChange}
					className="flex-1 text-sm border-b border-primary-300 bg-transparent outline-none py-0.5"
					autoFocus
					onKeyDown={(e) => {
						if (e.key === 'Enter') onUpdate();
						if (e.key === 'Escape') onCancelEdit();
					}}
				/>
				<button onClick={onUpdate} className="text-success-700 hover:text-success-700 p-0.5">
					<BsCheck2 className="w-4 h-4" />
				</button>
				<button onClick={onCancelEdit} className="text-bgBase-800 hover:text-text p-0.5">
					<BsXLg className="w-3 h-3" />
				</button>
			</>
		);
	}

	return (
		<>
			<span className="text-sm font-medium flex-1 truncate text-text">{tag.name}</span>

			{typeof tag.usageCount === 'number' && (
				<span className="text-[10px] text-bgBase-800 bg-bgBase-400 rounded-full px-1.5 py-0.5 flex-shrink-0">
					{tag.usageCount} {tag.usageCount === 1 ? 'Firma' : 'Firmen'}
				</span>
			)}

			<IconButton icon={BsPencil} label="Umbenennen" onClick={onStartEdit} className="h-6 w-6" />

			<IconButton icon={BsTrash} label="Löschen" tone="danger" onClick={onDelete} className="h-6 w-6" />
		</>
	);
};

TagSettingsRow.propTypes = {
	tag: PropTypes.object.isRequired,
	isEditing: PropTypes.bool.isRequired,
	editName: PropTypes.string.isRequired,
	onEditNameChange: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onCancelEdit: PropTypes.func.isRequired,
	onStartEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
};

const TagsSettingsTab = () => {
	const { data, isLoading } = useTags({ withUsage: true });
	const tags = [...(data?.tags ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'de'));
	const showError = useErrorStore((s) => s.showError);

	const createTag = useCreateTag();
	const renameTag = useRenameTag();
	const deleteTag = useDeleteTag();

	// Add form state
	const [showAdd, setShowAdd] = useState(false);
	const [newName, setNewName] = useState('');

	// Edit state
	const [editingId, setEditingId] = useState(null);
	const [editName, setEditName] = useState('');
	const [deleteTarget, setDeleteTarget] = useState(null);

	// CRUD handlers

	const handleCreate = async () => {
		const name = newName.trim();
		if (!name) return;
		try {
			await createTag.mutateAsync({ name });
			setShowAdd(false);
			setNewName('');
		} catch (err) {
			showError({ error: err, title: 'Tag konnte nicht erstellt werden' });
		}
	};

	const handleUpdate = async (id) => {
		const name = editName.trim();
		if (!name) return;
		try {
			await renameTag.mutateAsync({ id, name });
			setEditingId(null);
		} catch (err) {
			showError({ error: err, title: 'Tag konnte nicht umbenannt werden' });
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteTag.mutateAsync(deleteTarget.id);
		} catch (err) {
			showError({ error: err, title: 'Tag konnte nicht gelöscht werden' });
		} finally {
			setDeleteTarget(null);
		}
	};

	const startEdit = (tag) => {
		setEditingId(tag.id);
		setEditName(tag.name);
	};

	return (
		<div className="space-y-4">
			<div>
				<h2 className="text-sm font-semibold text-text">Tags</h2>
				<p className="text-xs text-bgBase-800">
					Erstelle, benenne oder lösche Tags, mit denen du deine Bestandskunden kategorisierst.
				</p>
			</div>

			{isLoading && <p className="text-sm text-bgBase-800">Wird geladen …</p>}

			<div className="space-y-1">
				{tags.map((tag) => (
					<Card key={tag.id} className="flex items-center gap-2.5 px-3 py-2">
						<TagSettingsRow
							tag={tag}
							isEditing={editingId === tag.id}
							editName={editName}
							onEditNameChange={(e) => setEditName(e.target.value)}
							onUpdate={() => handleUpdate(tag.id)}
							onCancelEdit={() => setEditingId(null)}
							onStartEdit={() => startEdit(tag)}
							onDelete={() => setDeleteTarget(tag)}
						/>
					</Card>
				))}

				{!isLoading && tags.length === 0 && !showAdd && (
					<p className="text-sm text-bgBase-800 italic py-4 text-center">Noch keine Tags angelegt.</p>
				)}
			</div>

			{showAdd ? (
				<div className="rounded-card border border-primary-200 bg-primary-100 p-3 space-y-3">
					<div>
						<label className="block text-xs font-medium text-text mb-1">Name *</label>
						<TextInput
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							placeholder="z. B. VIP-Kunde"
							autoFocus
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleCreate();
								if (e.key === 'Escape') setShowAdd(false);
							}}
						/>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>
							Abbrechen
						</Button>
						<Button
							variant="solid"
							size="sm"
							onClick={handleCreate}
							loading={createTag.isPending}
							disabled={!newName.trim()}
						>
							Tag hinzufügen
						</Button>
					</div>
				</div>
			) : (
				<Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
					<BsPlus className="w-4 h-4 mr-1" />
					Tag hinzufügen
				</Button>
			)}

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDelete}
				title="Tag löschen"
				message={
					<>
						Tag <strong>&quot;{deleteTarget?.name}&quot;</strong> wirklich löschen? Er wird von allen Firmen
						entfernt.
					</>
				}
				isLoading={deleteTag.isPending}
			/>
		</div>
	);
};

export default TagsSettingsTab;
